import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import {
  AUTO_IMPORT_TARGETS,
  CLASSIFICATION_IMPORT_TARGETS,
  DraftResponse,
  IMPORT_DATE_FORMATS,
  MapContextResponse,
  OPTIONAL_IMPORT_TARGETS,
  REQUIRED_IMPORT_TARGETS,
  importLabelFor,
} from './import.models';

@Component({
  selector: 'app-import-map',
  imports: [FormsModule],
  template: `
    <section class="card compact">
      <h1>Map CSV Columns</h1>
      @if (message()) {
        <div class="msg" [class.err]="isError()">{{ message() }}</div>
      }

      @if (draft()?.hasDraft) {
        <p class="hint">File: <b>{{ draft()?.fileName }}</b>. The system will auto-map where possible. You can adjust mappings and leave optional fields blank.</p>
        <p class="hint">VM Direct API mode: <code>RatePlan</code> maps to VM <b>Discount Code</b>. Leaving it blank is allowed.</p>

        <form class="form" (submit)="dryRun($event)">
          <div class="top-grid">
            <section class="top-card compact-card">
              <label>Preset</label>
              <div class="top-actions">
                <select [(ngModel)]="selectedPresetId" name="presetId">
                  <option [ngValue]="0">(None)</option>
                  @for (preset of presets(); track preset.id) {
                    <option [ngValue]="preset.id">{{ preset.name }}</option>
                  }
                </select>
                <button type="button" (click)="applyPreset()" [disabled]="busy()">Apply preset</button>
                <button type="button" class="secondary" (click)="startOver()" [disabled]="busy()">Start over</button>
              </div>
            </section>

            <section class="top-card compact-card">
              <label>Date format (Arrival/Departure)</label>
              <select [ngModel]="dateFormat" name="dateFormat" [class.invalid]="showValidation() && !dateFormat.trim()" (ngModelChange)="onDateFormatChange($event)">
                <option value="">(Choose format...)</option>
                @for (option of dateFormats; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
              <label class="confirm-date" [class.invalid-text]="showValidation() && !dateFormatConfirmed">
                <input type="checkbox" [ngModel]="dateFormatConfirmed" name="dateFormatConfirmed" [disabled]="!dateFormat" (ngModelChange)="dateFormatConfirmed = !!$event">
                I confirmed this date format matches my CSV.
              </label>
              @if ((draft()?.dateSamplePreview || []).length > 0) {
                <div class="hint">
                  Examples: <code>{{ (draft()?.dateSamplePreview || []).join(', ') }}</code><br>
                  Suggested: <b>{{ suggestedDateFormatLabel() }}</b>
                </div>
                @if (isDateSampleAmbiguous()) {
                  <div class="hint warn">Ambiguous date sample detected (values parse as both M/D and D/M). Explicit confirmation is required.</div>
                }
                @if (selectedDatePreview().length > 0) {
                  <div class="hint">
                    Parsed preview:
                    <code>{{ selectedDatePreview().join(' · ') }}</code>
                  </div>
                }
              } @else {
                <div class="hint">No date-like sample values detected; choose the date format explicitly.</div>
              }
            </section>
          </div>

          <section class="room-card compact-card">
            <label>Room Number Validation (VM Direct API)</label>
            <div class="hint">Applies only when <code>RoomNumber</code> is mapped. Dry Run will validate room/type pairing and room assignment overlaps.</div>
            <div class="radio-row">
              <label><input [(ngModel)]="roomValidationMode" name="roomValidationMode" type="radio" value="fail"> Fail import on room conflicts</label>
              <label><input [(ngModel)]="roomValidationMode" name="roomValidationMode" type="radio" value="strip"> Strip conflicting room numbers and continue</label>
            </div>
            @if (vmSessionActive()) {
              <div class="hint">VM auth active for property <code>{{ vmPropertyId() }}</code>@if (vmUsername()) { <span> · user <code>{{ vmUsername() }}</code></span> }.</div>
            }
          </section>

          <section class="preset-save compact-card">
            <label><input [(ngModel)]="savePreset" name="savePreset" type="checkbox"> Save these mappings as a preset</label>
            <input [(ngModel)]="presetName" name="presetName" type="text" placeholder="Preset name (if saving)">
            <div class="hint">Anyone can create presets. Only admins can rename or delete presets.</div>
          </section>

          <h2>Required fields</h2>
          <div class="hint">These must be mapped from the CSV.</div>
          <div class="mapping-grid">
            <div class="head">DB field</div><div class="head">CSV header</div><div class="head">Notes</div>
            @for (target of requiredTargets; track target) {
              <div><label class="field-label required-label" [class.invalid-text]="isRequiredMissing(target)"><span class="req">*</span><span>{{ labelFor(target) }}</span></label></div>
              <div>
                <select [ngModel]="localMapping[target] || ''" [name]="'required-' + target" [class.invalid]="isRequiredMissing(target)" (ngModelChange)="localMapping[target] = $event">
                  <option value="">(Unmapped)</option>
                  @for (h of draft()?.headers || []; track h) {
                    <option [value]="h">{{ h }}</option>
                  }
                </select>
              </div>
              <div class="hint" [class.invalid-text]="isRequiredMissing(target)">Required - must be mapped.</div>
            }
          </div>

          <h2>VM Reservation Classifications</h2>
          <div class="hint">Each field must be mapped from CSV or set as a default from VM.</div>
          <div class="mapping-grid">
            <div class="head">VM field</div><div class="head">CSV header</div><div class="head">Default from VM</div>
            @for (target of classificationTargets; track target) {
              <div><label>{{ labelFor(target) }}</label></div>
              <div>
                <select [ngModel]="localMapping[target] || ''" [name]="'classification-' + target" [class.invalid]="isClassificationMissing(target)" (ngModelChange)="localMapping[target] = $event">
                  <option value="">(Unmapped)</option>
                  @for (h of draft()?.headers || []; track h) {
                    <option [value]="h">{{ h }}</option>
                  }
                </select>
              </div>
              <div>
                <select [ngModel]="localDefaults[target] ?? ''" [name]="'default-' + target" [class.invalid]="isClassificationMissing(target)" (ngModelChange)="localDefaults[target] = $event">
                  <option value="">(No default)</option>
                  @for (option of vmOptions()[target] || []; track option.id) {
                    <option [value]="option.id">{{ option.label }}</option>
                  }
                </select>
                @if (isClassificationMissing(target)) {
                  <div class="hint invalid-text">Map a CSV header or choose a default.</div>
                }
                @if ((vmOptions()[target] || []).length === 0) {
                  <div class="hint">No VM options were found for this field from setup endpoints.</div>
                }
              </div>
            }
          </div>

          <h2>Optional fields</h2>
          <div class="hint">These can be mapped if your file includes them. If provided, Country will be normalized during import.</div>
          <div class="mapping-grid">
            <div class="head">DB field</div><div class="head">CSV header</div><div class="head">Notes</div>
            @for (target of optionalTargets; track target) {
              <div><label>{{ labelFor(target) }}</label></div>
              <div>
                <select [ngModel]="localMapping[target] || ''" [name]="'optional-' + target" (ngModelChange)="localMapping[target] = $event">
                  <option value="">(Unmapped)</option>
                  @for (h of draft()?.headers || []; track h) {
                    <option [value]="h">{{ h }}</option>
                  }
                </select>
              </div>
              <div class="hint">Optional.</div>
            }
          </div>

          <h2>Auto-calculated fields</h2>
          <div class="hint">If blank or unmapped, the system will calculate or fill these automatically on import.</div>
          <div class="mapping-grid">
            <div class="head">Field</div><div class="head">CSV header</div><div class="head">Notes</div>
            @for (target of autoTargets; track target) {
              <div><label>{{ labelFor(target) }}</label></div>
              <div>
                <select [ngModel]="localMapping[target] || ''" [name]="'auto-' + target" (ngModelChange)="localMapping[target] = $event">
                  <option value="">(Unmapped)</option>
                  @for (h of draft()?.headers || []; track h) {
                    <option [value]="h">{{ h }}</option>
                  }
                </select>
              </div>
              <div class="hint">Auto-calculated if blank or unmapped.</div>
            }
          </div>

          <div class="actions-block">
            @if (actionError()) {
              <div class="action-msg err">{{ actionError() }}</div>
            }
            <div class="actions">
              <button type="submit" [disabled]="busy()">{{ busy() ? 'Working…' : 'Dry Run' }}</button>
            </div>
          </div>
        </form>
      }
    </section>
  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:12px;padding:14px 16px;display:grid;gap:10px;max-width:980px;margin:0 auto}
    h1{margin:0;font-size:1.95rem;line-height:1.15}
    h2{margin:2px 0 0;font-size:1.45rem;line-height:1.2}
    .form{display:grid;gap:12px}
    .hint{color:var(--muted);font-size:12px;line-height:1.35}
    .msg{padding:10px 12px;border-radius:10px;background:#eef4ff;color:#0a2f6b;border:1px solid #c7d7f5}
    .msg.err{background:#fdecec;color:#8a1f1f;border-color:#f2c4c4}
    .top-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
    .top-card,.room-card,.preset-save{border:1px solid #dbe5ff;border-radius:10px;padding:10px;background:#f8fbff;display:grid;gap:6px}
    .top-actions,.actions,.radio-row{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
    .actions-block{display:grid;gap:8px}
    .action-msg{padding:9px 11px;border-radius:8px;font-size:13px}
    .action-msg.err{background:#fdecec;color:#8a1f1f;border:1px solid #f2c4c4}
    .preset-save input[type="text"]{height:34px}
    .mapping-grid{display:grid;grid-template-columns:180px 1fr 1fr;gap:8px;align-items:start}
    .head{font-size:11px;color:#4e5c75;text-transform:uppercase;letter-spacing:.03em;font-weight:700}
    .req{color:#a42323}
    label{display:grid;gap:4px;font-weight:600;color:#24334a;font-size:13px}
    .field-label{display:flex;align-items:center;gap:6px}
    .required-label .req{line-height:1}
    select,input{height:34px;border:1px solid #c8d2e1;border-radius:8px;padding:0 10px;font-size:13px}
    input[type="checkbox"]{width:16px;height:16px}
    input[type="radio"]{width:14px;height:14px;accent-color:#2563eb}
    .radio-row label{display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:700}
    button{height:34px;border:0;border-radius:8px;background:var(--primary);color:#fff;font-weight:700;padding:0 12px;cursor:pointer;font-size:13px}
    .secondary{background:#6b778c}
    .compact-card label{font-size:12px}
    .confirm-date{display:flex;align-items:center;gap:6px;font-size:12px}
    .warn{color:#8a1f1f;font-weight:700}
    select.invalid{border-color:#c62828;background:#fdecec;box-shadow:0 0 0 2px rgba(198,40,40,.18)}
    .invalid-text{color:#8a1f1f;font-weight:700}
    @media (max-width: 900px){
      .card{max-width:none}
      h1{font-size:1.65rem}
      h2{font-size:1.3rem}
      .top-grid,.mapping-grid{grid-template-columns:1fr}
    }
  `,
})
export class ImportMapComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  readonly busy = signal(false);
  readonly message = signal('');
  readonly actionError = signal('');
  readonly isError = signal(false);
  readonly showValidation = signal(false);
  readonly draft = signal<DraftResponse | null>(null);
  readonly presets = signal<MapContextResponse['presets']>([]);
  readonly vmOptions = signal<MapContextResponse['optionSets']>({ MarketSegmentId: [], ReasonForStayId: [], ReferralMethodId: [] });
  readonly vmSessionActive = signal(false);
  readonly vmPropertyId = signal('');
  readonly vmUsername = signal('');

  readonly dateFormats = IMPORT_DATE_FORMATS;
  readonly requiredTargets = REQUIRED_IMPORT_TARGETS;
  readonly classificationTargets = CLASSIFICATION_IMPORT_TARGETS;
  readonly optionalTargets = OPTIONAL_IMPORT_TARGETS;
  readonly autoTargets = AUTO_IMPORT_TARGETS;

  localMapping: Record<string, string> = {};
  localDefaults: Record<string, string | number | null> = {};
  dateFormat = '';
  suggestedDateFormat = '';
  dateFormatConfirmed = false;
  roomValidationMode: 'fail' | 'strip' = 'fail';
  selectedPresetId = 0;
  savePreset = false;
  presetName = '';

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  private async load(): Promise<void> {
    this.busy.set(true);
    try {
      const res = await firstValueFrom(this.api.get<MapContextResponse>('/import/map-context'));
      if (!res.draft?.hasDraft) {
        await this.router.navigateByUrl('/import/upload');
        return;
      }
      if (!res.vmSession.active) {
        await this.router.navigateByUrl('/import/vm-auth?next=map');
        return;
      }
      this.draft.set(res.draft);
      this.presets.set(res.presets);
      this.vmOptions.set(res.optionSets);
      this.vmSessionActive.set(Boolean(res.vmSession.active));
      this.vmPropertyId.set(res.vmSession.propertyId || '');
      this.vmUsername.set(res.vmSession.username || '');
      this.localMapping = { ...(res.draft.mapping || {}) };
      this.localDefaults = { ...(res.draft.defaults || {}) };
      this.suggestedDateFormat = String(res.draft.suggestedDateFormat || res.draft.dateFormat || '').trim();
      this.dateFormat = res.draft.dateFormatConfirmed ? String(res.draft.dateFormat || '') : '';
      this.dateFormatConfirmed = Boolean(res.draft.dateFormatConfirmed && this.dateFormat);
      this.roomValidationMode = res.draft.roomValidation?.mode || 'fail';
      this.selectedPresetId = Number(res.draft.preset?.selectedId || 0);
      this.savePreset = Boolean(res.draft.preset?.save);
      this.presetName = res.draft.preset?.name || '';
    } catch {
      this.isError.set(true);
      this.message.set('Failed to load mapping screen.');
    } finally {
      this.busy.set(false);
    }
  }

  async applyPreset(): Promise<void> {
    if (!this.selectedPresetId) return;
    this.busy.set(true);
    this.message.set('');
    this.isError.set(false);
    try {
      const res = await firstValueFrom(this.api.post<{ ok: boolean; error?: string }>('/import/apply-preset', { selectedId: this.selectedPresetId }));
      if (!res.ok) {
        this.isError.set(true);
        this.message.set(res.error || 'Failed to apply preset.');
        return;
      }
      await this.load();
    } catch {
      this.isError.set(true);
      this.message.set('Failed to apply preset.');
    } finally {
      this.busy.set(false);
    }
  }

  async dryRun(event: Event): Promise<void> {
    event.preventDefault();
    const precheckErrors = this.getDryRunPrecheckErrors();
    if (precheckErrors.length > 0) {
      this.isError.set(true);
      this.message.set(precheckErrors.join(' '));
      this.actionError.set(`Cannot run Dry Run yet. ${precheckErrors.length} mapping issue${precheckErrors.length === 1 ? '' : 's'} must be fixed.`);
      this.showValidation.set(true);
      this.scrollToFirstInvalid();
      return;
    }

    this.showValidation.set(false);
    this.busy.set(true);
    this.message.set('');
    this.actionError.set('');
    this.isError.set(false);
    try {
      const saveRes = await firstValueFrom(
        this.api.post<{ ok: boolean; error?: string }>('/import/map', {
          mapping: this.localMapping,
          defaults: this.localDefaults,
          dateFormat: this.dateFormat,
          dateFormatConfirmed: this.dateFormatConfirmed,
          roomValidationMode: this.roomValidationMode,
          preset: {
            selectedId: this.selectedPresetId || null,
            save: this.savePreset,
            name: this.presetName,
          },
        }),
      );
      if (!saveRes.ok) {
        this.isError.set(true);
        this.message.set(saveRes.error || 'Failed to save mapping.');
        this.actionError.set('Dry Run could not start. See error details above.');
        this.scrollToTop();
        return;
      }
      await this.router.navigateByUrl('/import/validate');
    } catch {
      this.isError.set(true);
      this.message.set('Failed to start dry run.');
      this.actionError.set('Dry Run could not start. See error details above.');
      this.scrollToTop();
    } finally {
      this.busy.set(false);
    }
  }

  async startOver(): Promise<void> {
    await firstValueFrom(this.api.post('/import/reset', {}));
    await this.router.navigateByUrl('/import/upload');
  }

  onDateFormatChange(value: string): void {
    const next = String(value || '').trim();
    if (this.dateFormat !== next) {
      this.dateFormatConfirmed = false;
    }
    this.dateFormat = next;
  }

  suggestedDateFormatLabel(): string {
    if (!this.suggestedDateFormat) return 'Unknown';
    return this.dateFormats.find((item) => item.value === this.suggestedDateFormat)?.label ?? this.suggestedDateFormat;
  }

  selectedDatePreview(): string[] {
    const selected = String(this.dateFormat || '').trim();
    if (!selected) return [];
    const samples = (this.draft()?.dateSamplePreview || []).slice(0, 5);
    const out: string[] = [];
    for (const raw of samples) {
      const iso = this.tryParseDateByFormat(raw, selected);
      out.push(`${raw} -> ${iso || 'invalid'}`);
    }
    return out;
  }

  isDateSampleAmbiguous(): boolean {
    const samples = this.draft()?.dateSamplePreview || [];
    if (samples.length === 0) return false;
    const pairs: Array<[string, string]> = [
      ['n/j/y', 'j/n/y'],
      ['n/j/Y', 'j/n/Y'],
      ['m/d/y', 'd/m/y'],
      ['m/d/Y', 'd/m/Y'],
    ];
    for (const raw of samples) {
      for (const [a, b] of pairs) {
        const pa = this.tryParseDateByFormat(raw, a);
        const pb = this.tryParseDateByFormat(raw, b);
        if (pa && pb && pa !== pb) return true;
      }
    }
    return false;
  }

  labelFor(field: string): string {
    return importLabelFor(field);
  }

  isRequiredMissing(target: string): boolean {
    return this.showValidation() && !String(this.localMapping[target] ?? '').trim();
  }

  isClassificationMissing(target: string): boolean {
    if (!this.showValidation()) return false;
    const mappedHeader = String(this.localMapping[target] ?? '').trim();
    const defaultValue = this.localDefaults[target];
    const hasDefault = !(defaultValue === null || defaultValue === undefined || String(defaultValue).trim() === '');
    return !mappedHeader && !hasDefault;
  }

  private getDryRunPrecheckErrors(): string[] {
    const errors: string[] = [];
    if (!String(this.dateFormat || '').trim()) {
      errors.push('Date format must be selected before Dry Run.');
    }
    if (!this.dateFormatConfirmed) {
      errors.push('Date format must be explicitly confirmed before Dry Run.');
    }
    for (const target of this.requiredTargets) {
      const mappedHeader = String(this.localMapping[target] ?? '').trim();
      if (!mappedHeader) {
        errors.push(`Required field '${this.labelFor(target)}' must be mapped.`);
      }
    }

    for (const target of this.classificationTargets) {
      const mappedHeader = String(this.localMapping[target] ?? '').trim();
      const defaultValue = this.localDefaults[target];
      const hasDefault = !(defaultValue === null || defaultValue === undefined || String(defaultValue).trim() === '');
      if (!mappedHeader && !hasDefault) {
        errors.push(`${this.labelFor(target)} must be mapped from CSV or set as a default.`);
      }
    }
    return errors;
  }

  private tryParseDateByFormat(rawValue: string, format: string): string | null {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;
    const monthMap: Record<string, number> = {
      jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
      jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
    };
    let y = 0;
    let m = 0;
    let d = 0;
    let match: RegExpMatchArray | null = null;
    const toYear = (val: string, twoDigit: boolean): number => {
      const n = Number(val);
      if (!Number.isFinite(n)) return 0;
      if (!twoDigit) return n;
      return n >= 70 ? 1900 + n : 2000 + n;
    };

    switch (format) {
      case 'Y-m-d':
        match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/); if (!match) return null;
        y = toYear(match[1], false); m = Number(match[2]); d = Number(match[3]); break;
      case 'y-m-d':
        match = raw.match(/^(\d{2})-(\d{1,2})-(\d{1,2})$/); if (!match) return null;
        y = toYear(match[1], true); m = Number(match[2]); d = Number(match[3]); break;
      case 'd/m/Y':
        match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); if (!match) return null;
        d = Number(match[1]); m = Number(match[2]); y = toYear(match[3], false); break;
      case 'd/m/y':
        match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/); if (!match) return null;
        d = Number(match[1]); m = Number(match[2]); y = toYear(match[3], true); break;
      case 'j/n/Y':
        match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); if (!match) return null;
        d = Number(match[1]); m = Number(match[2]); y = toYear(match[3], false); break;
      case 'j/n/y':
        match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/); if (!match) return null;
        d = Number(match[1]); m = Number(match[2]); y = toYear(match[3], true); break;
      case 'm/d/Y':
        match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); if (!match) return null;
        m = Number(match[1]); d = Number(match[2]); y = toYear(match[3], false); break;
      case 'm/d/y':
        match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/); if (!match) return null;
        m = Number(match[1]); d = Number(match[2]); y = toYear(match[3], true); break;
      case 'n/j/Y':
        match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); if (!match) return null;
        m = Number(match[1]); d = Number(match[2]); y = toYear(match[3], false); break;
      case 'n/j/y':
        match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/); if (!match) return null;
        m = Number(match[1]); d = Number(match[2]); y = toYear(match[3], true); break;
      case 'd-m-Y':
        match = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/); if (!match) return null;
        d = Number(match[1]); m = Number(match[2]); y = toYear(match[3], false); break;
      case 'm-d-Y':
        match = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/); if (!match) return null;
        m = Number(match[1]); d = Number(match[2]); y = toYear(match[3], false); break;
      case 'd.m.Y':
        match = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/); if (!match) return null;
        d = Number(match[1]); m = Number(match[2]); y = toYear(match[3], false); break;
      case 'Y/m/d':
        match = raw.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/); if (!match) return null;
        y = toYear(match[1], false); m = Number(match[2]); d = Number(match[3]); break;
      case 'j-M-Y':
      case 'd-M-Y':
        match = raw.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/); if (!match) return null;
        d = Number(match[1]); m = monthMap[String(match[2]).toLowerCase()] || 0; y = toYear(match[3], false); break;
      default:
        return null;
    }

    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
    const dt = new Date(Date.UTC(y, m - 1, d));
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() + 1 !== m || dt.getUTCDate() !== d) return null;
    return `${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
  }

  private scrollToTop(): void {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private scrollToFirstInvalid(): void {
    if (typeof window === 'undefined') return;
    // Wait for change detection to apply the invalid classes before looking for them.
    setTimeout(() => {
      const first = document.querySelector<HTMLElement>('select.invalid, .confirm-date.invalid-text');
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}
